import SeoHead from "../components/SeoHead";
import { getSiteContent, localeRoutes } from "../i18n/siteContent";

const casesByLocale = {
    en: [
        {
            title: "Scalable Digital Platform for Industrial Events",
            context: "Digital ecosystem for MECSPE and other industrial fairs, integrating CRM, ticketing, exhibitor workflows, and live-event operations.",
            href: localeRoutes.en.eventPlatformCaseStudy,
            problem: [
                "Challenge: create a portal supported by a CMS, multiple enterprise integrations, exhibitor operations, and live-event usage under peak traffic",
                "Challenge: platform reliability had to hold while thousands of users accessed the system simultaneously with and admin panel to manage everything"
            ],
            approach: [
                "Solution: modular platform architecture with Salesforce sync, Vivaticket integration, exhibitor self-service, and PWA support during events",
                "Solution: centralized data synchronization and operational flows designed for real-world event pressure"
            ],
            outcome: [
                "Results: 64,753 registrations in 3 months, 65,000+ tickets generated, and 5,000+ daily visits",
                "Results: platform successfully supported real industrial events with thousands of concurrent users"
            ]
        },
        {
            title: "Transforming Engineering Delivery into a Scalable System",
            context: "Growth-stage company with delivery pressure and increasing platform complexity.",
            href: localeRoutes.en.engineeringCaseStudy,
            problem: [
                "Before: manual releases, fragile handovers, and limited project complexity the team could safely sustain",
                "Before: no CI discipline and uneven architecture standards across teams"
            ],
            approach: [
                "After: Docker baseline, CI pipelines, pragmatic Agile structure, and clearer engineering ownership",
                "After: React/headless architecture patterns introduced on critical delivery paths"
            ],
            outcome: [
                "Business impact: reduced deploy time and fewer production gaps",
                "Business impact: faster issue resolution and ability to acquire larger, more complex projects"
            ]
        },
        {
            title: "Cloud spend and reliability reset (AWS)",
            context: "International team scaling quickly with fragmented cloud patterns.",
            problem: [
                "Cloud cost growth not matched by business output",
                "Operational complexity reducing team velocity"
            ],
            approach: [
                "Defined workload-level cost governance and architecture baselines",
                "Prioritized reliability improvements on critical paths"
            ],
            outcome: [
                "More predictable cloud spend",
                "Faster decision cycles for platform investments"
            ]
        },
        {
            title: "Delivery execution and team structure evolution",
            context: "Product organization with good talent but inconsistent execution rhythm.",
            problem: [
                "Unclear ownership across teams",
                "Planning and execution not aligned with business priorities"
            ],
            approach: [
                "Redefined team responsibilities and delivery operating model",
                "Set pragmatic agile rituals focused on outcomes"
            ],
            outcome: [
                "Better cross-team coordination",
                "Higher delivery consistency over time"
            ]
        },
        {
            title: "Low automation and manual deployments",
            context: "Engineering team with growth pressure and frequent release requests.",
            problem: [
                "Too many manual deployment steps",
                "Releases dependent on a few senior people"
            ],
            approach: [
                "Introduced CI/CD pipelines with standard release checks",
                "Documented and simplified deployment flow across teams"
            ],
            outcome: [
                "Faster and repeatable releases",
                "Lower release risk and reduced operational stress"
            ]
        },
        {
            title: "Inconsistent development environments",
            context: "Multiple teams using different local tools and setup processes.",
            problem: [
                "Environment mismatches between developers and QA",
                "Missing Docker setup and hard-to-reproduce bugs"
            ],
            approach: [
                "Defined a shared Docker-based development baseline",
                "Standardized toolchain and onboarding workflow"
            ],
            outcome: [
                "Fewer 'works on my machine' issues",
                "Faster onboarding and smoother team collaboration"
            ]
        },
        {
            title: "No clear delivery metrics and decision framework",
            context: "Leadership team lacked visibility on delivery health and bottlenecks.",
            problem: [
                "Reports without actionable delivery indicators",
                "Decisions made reactively under pressure"
            ],
            approach: [
                "Introduced a small set of delivery KPIs tied to business goals",
                "Created a simple decision cadence across leadership and teams"
            ],
            outcome: [
                "Faster, evidence-based decisions",
                "Better prioritization and more predictable execution"
            ]
        }
    ],
    it: [
        {
            title: "Piattaforma digitale scalabile per eventi fieristici",
            context: "Ecosistema digitale per MECSPE e altre fiere industriali, con CRM, CMS, ticketing, area espositori e supporto operativo durante l'evento.",
            href: localeRoutes.it.eventPlatformCaseStudy,
            problem: [
                "Sfida: creare un portale supportato da un CMS con più integrazioni enterprise, flussi espositori e uso live durante l'evento in condizioni di picco",
                "Sfida: la piattaforma doveva restare affidabile mentre migliaia di utenti accedevano contemporaneamente con un pannello di controllo centrale per gestire tutto"
            ],
            approach: [
                "Soluzione: architettura modulare con sincronizzazione Salesforce, integrazione Vivaticket, area self-service espositori e supporto PWA durante gli eventi",
                "Soluzione: sincronizzazione dati centralizzata e flussi operativi progettati per la pressione reale degli eventi"
            ],
            outcome: [
                "Risultati: 64.753 registrazioni in 3 mesi, 65.000+ ticket generati e oltre 5.000 visite giornaliere",
                "Risultati: piattaforma usata con successo in eventi industriali reali con migliaia di utenti simultanei"
            ]
        },
        {
            title: "Trasformare la delivery engineering in un sistema scalabile",
            context: "Azienda in crescita con pressione sulla delivery e complessità tecnica in aumento.",
            href: localeRoutes.it.engineeringCaseStudy,
            problem: [
                "Prima: rilasci manuali, flusso fragile e limite alla complessità dei progetti gestibili",
                "Prima: assenza di disciplina CI e standard architetturali non omogenei"
            ],
            approach: [
                "Dopo: base Docker, pipeline CI, struttura Agile pragmatica e ownership engineering più chiara",
                "Dopo: introduzione di pattern React/headless nei percorsi critici di delivery"
            ],
            outcome: [
                "Impatto business: riduzione dei tempi di deploy e meno gap in produzione",
                "Impatto business: risoluzione issue più rapida e capacità di acquisire progetti più grandi e complessi"
            ]
        },
        {
            title: "Reset costi cloud e affidabilità (AWS)",
            context: "Team internazionale in crescita con uso del cloud poco ordinato.",
            problem: [
                "Costi cloud in aumento",
                "Processi troppo complessi che rallentano il team"
            ],
            approach: [
                "Regole semplici per controllare i costi cloud",
                "Interventi mirati sui punti più critici"
            ],
            outcome: [
                "Costi più sotto controllo",
                "Decisioni più veloci sulla piattaforma"
            ]
        },
        {
            title: "Evoluzione execution delivery e struttura team",
            context: "Team con buone competenze ma consegne non regolari.",
            problem: [
                "Ruoli e responsabilità poco chiari",
                "Piani non allineati alle priorità di business"
            ],
            approach: [
                "Nuova organizzazione dei team e del lavoro",
                "Metodo agile pratico, orientato ai risultati"
            ],
            outcome: [
                "Migliore collaborazione tra team",
                "Consegne più costanti nel tempo"
            ]
        },
        {
            title: "Poca automazione e deploy manuali",
            context: "Team engineering con molte richieste di rilascio e processi ancora manuali.",
            problem: [
                "Troppi passaggi manuali nei deploy",
                "Rilasci dipendenti da poche persone esperte"
            ],
            approach: [
                "Introdotte pipeline CI/CD con controlli standard",
                "Processo di rilascio semplificato e documentato"
            ],
            outcome: [
                "Rilasci più veloci e ripetibili",
                "Meno rischio operativo e meno stress sul team"
            ]
        },
        {
            title: "Ambienti di sviluppo non allineati",
            context: "Più team con tool diversi e setup locali non uniformi.",
            problem: [
                "Differenze tra ambienti developer e QA",
                "Assenza di Docker e bug difficili da replicare"
            ],
            approach: [
                "Definita una base comune con Docker",
                "Toolchain e onboarding standardizzati"
            ],
            outcome: [
                "Meno problemi 'da me funziona'",
                "Onboarding più rapido e collaborazione più fluida"
            ]
        },
        {
            title: "Metriche di delivery poco chiare",
            context: "Leadership con poca visibilità sullo stato reale della delivery.",
            problem: [
                "Report non utili per decidere",
                "Decisioni prese in emergenza"
            ],
            approach: [
                "Introdotto un set minimo di KPI collegati agli obiettivi business",
                "Definita una cadenza decisionale semplice tra leadership e team"
            ],
            outcome: [
                "Decisioni più veloci e basate su dati",
                "Priorità più chiare e delivery più prevedibile"
            ]
        }
    ]
};

function CaseStudiesPage({ locale = "en" }) {
    const text = getSiteContent(locale).caseStudies;
    const site = getSiteContent(locale);
    const routes = localeRoutes[locale];
    const cases = casesByLocale[locale] || casesByLocale.en;
    const breadcrumbItems = [
        { name: site.nav.home, path: routes.home },
        { name: site.nav.caseStudies, path: routes.caseStudies }
    ];

    return (
        <>
            <SeoHead
                title={text.title}
                description={text.description}
                keywords="Maurizio Brioschi case studies, fractional cto case studies, solution architect projects, digital transformation examples, cloud strategy case study"
                path={routes.caseStudies}
                locale={locale}
                enPath={localeRoutes.en.caseStudies}
                itPath={localeRoutes.it.caseStudies}
                breadcrumbItems={breadcrumbItems}
            />
            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                <header>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{text.label}</p>
                    <h1 className="mt-3 text-4xl font-impact text-slate-900">{text.heading}</h1>
                    <p className="mt-4 max-w-3xl text-slate-600">{text.intro}</p>
                </header>

                <section className="mt-8 space-y-6">
                    {cases.map((item) => (
                        <article key={item.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-impact text-slate-900">{item.title}</h2>
                            <p className="mt-3 text-sm text-slate-600"><span className="font-semibold">{text.context}:</span> {item.context}</p>
                            <ul className="mt-4 space-y-2 text-sm text-slate-700">
                                <li><span className="font-semibold">{text.problem}:</span> {item.problem[0]}</li>
                                <li>{item.problem[1]}</li>
                            </ul>
                            <ul className="mt-4 space-y-2 text-sm text-slate-700">
                                <li><span className="font-semibold">{text.approach}:</span> {item.approach[0]}</li>
                                <li>{item.approach[1]}</li>
                            </ul>
                            <ul className="mt-4 space-y-2 text-sm text-slate-700">
                                <li><span className="font-semibold">{text.outcome}:</span> {item.outcome[0]}</li>
                                <li>{item.outcome[1]}</li>
                            </ul>
                            {item.href ? (
                                <a
                                    href={item.href}
                                    className="mt-5 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
                                >
                                    {text.featuredCta}
                                </a>
                            ) : null}
                        </article>
                    ))}
                </section>
            </main>
        </>
    );
}

export default CaseStudiesPage;
